/**
 * Per-page SEO tags: title, description, canonical URL, Open Graph, Twitter
 * card, and optional JSON-LD structured data. index.html carries sensible
 * site-wide defaults for non-JS crawlers (social link unfurlers); this
 * overrides them per-page once the app hydrates, which is what Google/Bing
 * (both execute JS) actually index.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ENV } from '@/config/environment';

export interface SEOProps {
  title: string;
  description?: string;
  path?: string; // e.g. "/blog/some-id" - defaults to current path
  image?: string; // absolute or site-relative URL
  type?: 'website' | 'article';
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path,
  image,
  type = 'website',
  noIndex = false,
  jsonLd,
}) => {
  const siteUrl = ENV.app.siteUrl;
  const fullTitle = title === ENV.app.name ? title : `${title} | ${ENV.app.name}`;
  const resolvedDescription = description || ENV.app.description;
  const resolvedPath = path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const url = `${siteUrl}${resolvedPath}`;
  const resolvedImage = image
    ? (image.startsWith('http') ? image : `${siteUrl}${image}`)
    : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:site_name" content={ENV.app.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      {resolvedImage && <meta property="og:image" content={resolvedImage} />}

      <meta name="twitter:card" content={resolvedImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {resolvedImage && <meta name="twitter:image" content={resolvedImage} />}

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
